import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import styled from "styled-components";

interface IPreProps {
 children?: ReactNode;
 data: unknown;
 wrap?: boolean;
 label?: string;
 style?: Pick<CSSProperties, "maxHeight" | "marginTop" | "flex" | "minHeight">;
}

const CopyButton = styled.button`
 display: inline-flex; align-items: center; justify-content: center;
 width: 30px; height: 30px; padding: 5px; border: 1px solid currentColor;
 border-radius: 6px; color: inherit; background: transparent; cursor: pointer; opacity: .7;
 &:hover { opacity: 1; background: #8882; }
 &:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; opacity: 1; }
 &:disabled { cursor: wait; opacity: .4; }
`;

const Pre = ({ children, data, wrap, label = "Raw", style }: IPreProps) => {
 const text = data == null ? "" : typeof data === "object" ? JSON.stringify(data, null, 2) : String(data);
 const [status, setStatus] = useState<"idle" | "copying" | "copied" | "error">("idle");
 useEffect(() => { setStatus("idle"); }, [text]);
 useEffect(() => {
  if (status !== "copied" && status !== "error") return;
  const timeout = setTimeout(() => setStatus("idle"), 2500);
  return () => clearTimeout(timeout);
 }, [status]);
 const copy = async () => {
  setStatus("copying");
  try {
   await navigator.clipboard.writeText(text);
   setStatus("copied");
  } catch { setStatus("error"); }
 };
 const message = status === "copied" ? "Скопировано" : status === "error" ? "Не удалось скопировать" : "";
 // Цвета Raw утверждены: фон rgb(34 36 47), текст rgb(67 255 73). Менять их в дальнейшем запрещено.
 return <div style={{ minWidth: 0, minHeight: 0, borderRadius: 8, background: "rgb(34 36 47)", color: "rgb(67 255 73)", display: "flex", flexDirection: "column", overflow: "hidden", ...style }}>
  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, padding: "8px 10px 0", flexShrink: 0 }}>
   <span role="status" aria-live="polite" style={{ fontSize: 12 }}>{message}</span>
   <CopyButton type="button" title={message || "Копировать"} aria-label={`Копировать ${label}`} disabled={status === "copying"} onClick={copy}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
     {status === "copied" ? <path d="m5 12 4 4L19 6" /> : <><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h4" /></>}
    </svg>
   </CopyButton>
  </div>
  <pre aria-label={label} tabIndex={0} style={{ margin: 0, padding: "10px 16px 16px", minHeight: 0, overflow: "auto", whiteSpace: wrap ? "pre-wrap" : "pre", overflowWrap: wrap ? "anywhere" : undefined, color: "inherit", font: "14px/1.6 Consolas, monospace" }}>{text}{children}</pre>
 </div>;
};

export default Pre;
